const DISPLAY_DELIMITER_PATTERNS = [
    [/^\\\[\s*([\s\S]*?)\s*\\\]$/, "$1"],
    [/^\$\$\s*([\s\S]*?)\s*\$\$$/, "$1"],
];

const DISPLAY_ENVIRONMENT_RE =
    /^\\begin\{(equation\*?|displaymath|align\*?|gather\*?|multline\*?)\}\s*([\s\S]*?)\s*\\end\{\1\}$/;
const INNER_DISPLAY_ENVIRONMENTS = new Set([
    "aligned",
    "gathered",
    "split",
    "array",
    "matrix",
    "pmatrix",
    "bmatrix",
    "cases",
]);

const RELATION_RE = /(\\(?:leq?|geq?|ne|neq|approx|equiv|sim|simeq|cong|propto)(?![A-Za-z])|[=<>])/;

function stripDisplayDelimiters(latex) {
    let content = String(latex || "").trim();

    for (const [pattern, replacement] of DISPLAY_DELIMITER_PATTERNS) {
        if (pattern.test(content)) {
            content = content.replace(pattern, replacement).trim();
            break;
        }
    }

    const environmentMatch = content.match(DISPLAY_ENVIRONMENT_RE);
    if (environmentMatch) {
        const [, environmentName, body] = environmentMatch;
        if (environmentName.startsWith("align")) {
            return `\\begin{aligned}\n${body.trim()}\n\\end{aligned}`;
        }
        if (environmentName.startsWith("gather") || environmentName.startsWith("multline")) {
            return `\\begin{gathered}\n${body.trim()}\n\\end{gathered}`;
        }
        return body.trim();
    }

    return content;
}

function startsWithInnerDisplayEnvironment(content) {
    const match = content.match(/^\\begin\{([^}]+)\}/);
    return Boolean(match && INNER_DISPLAY_ENVIRONMENTS.has(match[1]));
}

function alignAtFirstRelation(line) {
    if (line.includes("&")) {
        return line;
    }

    const match = line.match(RELATION_RE);
    if (!match) {
        return line;
    }

    const index = match.index ?? -1;
    if (index < 0) {
        return line;
    }

    const relation = match[0];
    const left = line.slice(0, index).trimEnd();
    const right = line.slice(index + relation.length).trimStart();

    return `${left} &${relation}${right ? ` ${right}` : ""}`;
}

export function normalizeMathOCRLatex(latex) {
    const content = stripDisplayDelimiters(latex)
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((line) => line.trimEnd())
        .join("\n")
        .trim();

    if (!content) {
        return "";
    }

    if (startsWithInnerDisplayEnvironment(content) || content.includes("\\\\")) {
        return content
            .split("\n")
            .filter((line) => line.trim().length)
            .join("\n");
    }

    const lines = content
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    if (lines.length <= 1) {
        return content;
    }

    const hasRelation = lines.some((line) => RELATION_RE.test(line));
    const environmentName = hasRelation ? "aligned" : "gathered";
    const normalizedLines = hasRelation ? lines.map(alignAtFirstRelation) : lines;

    return `\\begin{${environmentName}}\n${normalizedLines.join(" \\\\\n")}\n\\end{${environmentName}}`;
}

export function buildMathOCRDisplayBlock(latex) {
    const normalizedLatex = normalizeMathOCRLatex(latex);

    if (!normalizedLatex) {
        return "";
    }

    return `\n\\[\n${normalizedLatex}\n\\]\n`;
}
